import styled from 'styled-components';
import type { AlignmentValue } from '@elfsight/configurator-contracts';
import { useIsMobileBreakpoint } from '@elfsight/widget-utils';
import { CTAButtonType } from '../common';
import { MOBILE_BREAKPOINT } from './constants';
import { ButtonWithDirection } from './cta-buttons/button-with-direction';
import { ButtonWithShare } from './cta-buttons/button-with-share';
import { CommonButton } from './cta-buttons/common-button';
import { useLocationCardProps } from './location-card-settings-context';

export const CTAButtons = () => {
  const {
    ctaButtons,
    alignment,
    contactsAddress,
    businessName,
    contactsLocationName,
    contactsLocationAddress,
    editMode
  } = useLocationCardProps();
  const isMobileBreakpoint = useIsMobileBreakpoint(MOBILE_BREAKPOINT);
  const contactsName = typeof businessName === 'string' ? businessName : '';

  if (ctaButtons?.length <= 0) return null;

  return (
    <Container _alignment={alignment} _fullWidthButtons={isMobileBreakpoint}>
      {ctaButtons.map((button) => {
        const { link, type, text } = button;

        if (type === CTAButtonType.directions) {
          return (
            <ButtonWithDirection
              key={button?.id}
              ctaButton={button}
              isFullWidth={isMobileBreakpoint}
              contactsAddress={contactsLocationAddress || contactsAddress}
              contactsName={contactsLocationName || contactsName}
            />
          );
        }

        if (type === CTAButtonType.share) {
          return (
            <ButtonWithShare
              key={button?.id}
              ctaButton={button}
              isFullWidth={isMobileBreakpoint}
            />
          );
        }

        const extractedLink = String(
          typeof link === 'object' ? link?.rawValue : link
        );
        const extractedTarget =
          typeof link === 'object' ? link?.target : '_blank';

        let finalLink = extractedLink;
        let finalTarget = extractedTarget;

        if (editMode) {
          finalTarget = '_blank';
        }

        if (type === CTAButtonType.whatsapp) {
          finalLink = prepareWhatsappLink(extractedLink);
          finalTarget = '_blank';
        }

        if (button.type === CTAButtonType.call) {
          finalLink = `tel:${parsePhoneNumber(extractedLink)}`;
          finalTarget = '_self';
        }

        const clickHandler: () => void = () => {
          if (!finalLink) return;
          window.open(finalLink, finalTarget);
        };

        return (
          <CommonButton
            key={button?.id}
            ctaButton={button}
            onClick={clickHandler}
            fullWidth={isMobileBreakpoint}
          >
            {text}
          </CommonButton>
        );
      })}
    </Container>
  );
};

const linkRegExp = /(https?:\/\/)?([\w.-]+)\.([\w.]{2,6})([\w.-=?%+]*)\/?$/;

const parsePhoneNumber = (phone: string) => phone.replace(/[^0-9]/g, '');

const prepareWhatsappLink = (link: string) => {
  const isLink = linkRegExp.test(link);
  const parsedPhone = parsePhoneNumber(link);
  return isLink ? link : `https://wa.me/${parsedPhone}`;
};

const Container = styled.div<{
  _fullWidthButtons: boolean;
  _alignment: AlignmentValue;
}>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ _alignment }) => _alignment};
  flex-direction: ${({ _fullWidthButtons }) =>
    _fullWidthButtons ? 'column' : 'row'};
  gap: 12px;
`;
